import React, { useMemo, useRef, useState } from 'react';
import {
  mixRemoteTargetNow,
  sendRemoteAndroidTvAction,
  sendRemoteAssistantCommand,
  sendRemoteProtocolCommand,
  sendRemoteRoutedAction,
} from '../../components/Remote/deviceRemoteApi';
import { useSharedRemoteRuntime } from '../../components/Remote/useSharedRemoteRuntime';

// Use the same transports and command mappings as the D-pad, without loading
// launcher inventories or requesting screenshots for the compact Venue controls.
const transport = {
  sendAndroidTvRemoteAction: ({ deviceId, ...payload }) => sendRemoteAndroidTvAction({ targetDeviceId: deviceId, ...payload }),
  sendRoutedAction: ({ deviceId, ...payload }) => sendRemoteRoutedAction({ targetDeviceId: deviceId, ...payload }),
  sendProtocolCommand: ({ deviceId, ...payload }) => sendRemoteProtocolCommand({ targetDeviceId: deviceId, ...payload }),
  sendAssistantCommand: ({ deviceId, ...payload }) => sendRemoteAssistantCommand({ targetDeviceId: deviceId, ...payload }),
};

export default function VenuePlaybackControls({ entry, target, blocked = false, onRefresh }) {
  const device = useMemo(() => target ? {
    ...target,
    is_app: target.isApp === true,
    integration_data: { tv_controls: { connected_methods: target.capabilities?.availableMethods || [] } },
  } : null, [target]);
  const { handlePrimaryRemoteCommand, remoteAvailableCommands, selectedMethod } = useSharedRemoteRuntime({
    device, transport, previewEnabled: false,
  });
  const [mixBusy, setMixBusy] = useState(false);
  const mixInFlight = useRef(false);
  const supported = (command) => Boolean(target
    && selectedMethod !== 'webos' && remoteAvailableCommands?.[command]);
  const available = (command) => supported(command) && !blocked && !mixBusy;
  const spotifyAvailable = entry.isSpotify && target?.capabilities?.androidTvRemote?.paired
    && selectedMethod === 'android_tv_remote';

  const mixNow = async () => {
    if (blocked || entry.mixDisabled || mixInFlight.current || !target?.isApp) return;
    mixInFlight.current = true;
    setMixBusy(true);
    try {
      await mixRemoteTargetNow(target.id);
      onRefresh?.();
    } catch {
      // Keep the compact Venue controls quiet; release the button for retry.
    } finally {
      mixInFlight.current = false;
      setMixBusy(false);
    }
  };

  const control = (command, label, text = label) => (
    <button type="button" aria-label={label} disabled={!available(command)} onClick={() => handlePrimaryRemoteCommand(command)}>
      {text}
    </button>
  );

  return (
    <div className="venue-dashboard__playback-controls" aria-label={`Playback controls for ${entry.screenName}`}>
      <div className="venue-dashboard__control-row" aria-label="Volume controls">
        {control('vol_down', 'Volume down', 'Volume −')}
        {control('vol_up', 'Volume up', 'Volume +')}
        {control('mute', 'Mute')}
      </div>
      {spotifyAvailable ? (
        <div className="venue-dashboard__control-row" aria-label="Spotify controls">
          {supported('chan_down') ? control('chan_down', 'Previous track') : null}
          {supported('play_pause') ? control('play_pause', 'Play / Pause') : null}
          {supported('chan_up') ? control('chan_up', 'Next track') : null}
        </div>
      ) : null}
      {entry.isSuitestream ? (
        <button type="button" disabled={!target?.isApp || blocked || mixBusy || entry.mixDisabled} onClick={mixNow}>
          {mixBusy ? 'Mixing…' : 'Mix Now'}
        </button>
      ) : null}
    </div>
  );
}
